import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Save, Power, AlertTriangle } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Card, Input, Textarea, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from '../../../components/ui'

export default function CommunitySettingsPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showDeactivate, setShowDeactivate] = useState(false)
  const [deactivating, setDeactivating] = useState(false)
  const [form, setForm] = useState({ app_name: '', welcome_message: '' })

  useEffect(() => {
    axios.get(buildApiUrl('/v1/community/dashboard'))
      .then(res => {
        if (res.data.settings) {
          setForm({
            app_name: res.data.settings.app_name || '',
            welcome_message: res.data.settings.welcome_message || '',
          })
        }
      })
      .finally(() => setLoading(false))
  }, [])

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await axios.put(buildApiUrl('/v1/community/settings'), form)
      toast('success', 'Configuração salva')
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  async function handleDeactivate() {
    setDeactivating(true)
    try {
      await axios.put(buildApiUrl('/v1/community/settings'), { is_active: false })
      toast('success', 'Comunidade desativada')
      setShowDeactivate(false)
      navigate('/community/dashboard')
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro')
    } finally { setDeactivating(false) }
  }

  if (loading) return <div className="flex items-center justify-center py-16"><Spinner /></div>

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/community/dashboard')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuração da Comunidade</h1>
          <p className="text-gray-500 mt-1">Nome, mensagem de boas-vindas e status</p>
        </div>
      </div>

      <form onSubmit={handleSave}>
        <Card className="p-6 space-y-4">
          <Input
            label="Nome da comunidade"
            value={form.app_name}
            onChange={e => setForm(f => ({ ...f, app_name: e.target.value }))}
            placeholder="Ex: Tropa do Diogo"
            hint="Se vazio, usa o nome da organização"
          />
          <Textarea
            label="Mensagem de boas-vindas"
            value={form.welcome_message}
            onChange={e => setForm(f => ({ ...f, welcome_message: e.target.value }))}
            placeholder="Mensagem exibida na tela de login da comunidade"
            rows={3}
          />
          <div className="flex justify-end">
            <Button type="submit" loading={saving}>
              <Save className="w-4 h-4 mr-2" />
              Salvar
            </Button>
          </div>
        </Card>
      </form>

      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">Desativar comunidade</h3>
            <p className="text-sm text-gray-500 mt-1">A comunidade ficará inacessível para os membros. Os dados não serão apagados.</p>
          </div>
          <Button variant="danger" size="sm" onClick={() => setShowDeactivate(true)}>
            <Power className="w-4 h-4 mr-1.5" />
            Desativar
          </Button>
        </div>
      </Card>

      <Modal open={showDeactivate} onClose={() => setShowDeactivate(false)} size="sm">
        <ModalHeader onClose={() => setShowDeactivate(false)}>Desativar comunidade</ModalHeader>
        <ModalBody>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-error-500/10 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-error-500" />
            </div>
            <div>
              <p className="text-sm text-gray-700">Tem certeza? A comunidade ficará inacessível para os membros. Você poderá reativar a qualquer momento.</p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeactivate(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDeactivate} loading={deactivating}>Desativar</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
